





























































































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import Axios from 'axios';
import { accountingSheet } from '@/utils/config';
import { SheetEvent, SheetEventSeverity, SheetEventsApiResponse } from '@/types/sheetEvents';

/**
 * История обработки листа: что с ним происходило и почему он стоит.
 *
 * Лента живёт в собственной таблице ms_sheets.sheet_event, куда пишут ESB-коннекторы,
 * и не зависит от jBPM: у листов, переживших миграцию контейнеров, процесс уже не найти.
 *
 * Панель показывается только в карточке листа. Бейдж в списке листов делать нельзя —
 * это запрос на каждую строку при отрисовке, а листов больше четырёх тысяч.
 */
@Component
export default class SheetEventsDrawer extends Vue {
  @Prop({ default: null }) readonly sheetId!: number | null;

  open = false;

  loading = false;

  feed: SheetEventsApiResponse | null = null;

  loadError: string | null = null;

  unreadCount = 0;

  shownDetails: Array<number> = [];

  mounted(): void {
    this.loadEvents(false);
  }

  @Watch('sheetId')
  onSheetIdChanged(): void {
    this.feed = null;
    this.unreadCount = 0;
    this.loadEvents(false);
  }

  severityColor(severity: SheetEventSeverity): string {
    if (severity === 'error') {
      return 'error';
    }
    if (severity === 'warning') {
      return 'warning';
    }
    return 'grey lighten-1';
  }

  severityIcon(severity: SheetEventSeverity): string {
    if (severity === 'error') {
      return 'mdi-alert-circle';
    }
    if (severity === 'warning') {
      return 'mdi-alert';
    }
    return 'mdi-check';
  }

  /**
   * Время в ленте приходит из базы в UTC. Показываем в МСК с явной подписью:
   * расхождение поясов уже путало переписку с заказчиком.
   */
  formatMoscow(value: string | null): string {
    if (!value) {
      return '';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleString('ru-RU', {
      timeZone: 'Europe/Moscow',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  isDetailsShown(eventId: number): boolean {
    return this.shownDetails.includes(eventId);
  }

  toggleDetails(eventId: number): void {
    this.shownDetails = this.isDetailsShown(eventId)
      ? this.shownDetails.filter((id) => id !== eventId)
      : [...this.shownDetails, eventId];
  }

  openDrawer(): void {
    this.open = true;
    // markSeen=1 уходит только здесь: бейдж гаснет, когда ленту действительно открыли.
    this.loadEvents(true);
  }

  async loadEvents(markSeen: boolean): Promise<void> {
    if (!this.sheetId) {
      return;
    }
    this.loading = true;
    this.loadError = null;
    try {
      const response = await Axios.get<SheetEventsApiResponse>(
        accountingSheet.sheetEvents,
        { params: { sheetId: this.sheetId, markSeen: markSeen ? 1 : 0 } },
      );
      this.feed = response.data;
      // Счётчик приходит посчитанным на момент запроса, ДО сдвига курсора.
      // При открытии панели гасим его сразу: пользователь уже смотрит ленту.
      this.unreadCount = markSeen ? 0 : (response.data.unread_count || 0);
    } catch (e) {
      const axiosError = e as { response?: { data?: unknown }; message?: string };
      const responseData = axiosError.response?.data;
      this.loadError = typeof responseData === 'string'
        ? responseData
        : JSON.stringify(responseData ?? axiosError.message ?? e, null, 2);
    } finally {
      this.loading = false;
    }
  }

  copyDetails(event: SheetEvent): void {
    const parts = [
      event.message_ru,
      event.error_code ? `error_code: ${event.error_code}` : '',
      `source: ${event.source}`,
      this.sheetId ? `sheetId: ${this.sheetId}` : '',
      '',
      event.details || '',
    ].filter((part) => part !== '');
    navigator.clipboard.writeText(parts.join('\n'));
  }
}
